package org.example.project.enginepersonal

import org.example.project.settings.UISettings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.json.Json
import org.example.project.dashboard.LocalScreenRefreshVersion
import org.example.project.manager.LumaticaApiManager
import org.example.project.screens_cus.UrlaubsApi
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.service.ZustandManager
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.All
import org.example.project.utils.UserAvatarCircle
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text
import kotlin.js.Date

private val zeitJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = false
}

@Serializable
internal data class ZeitprofilPause(
    val id: String = "",
    val label: String = "Pause",
    val von: String = "12:00",
    val bis: String = "12:30"
)

@Serializable
internal data class PersonalZeitprofil(
    val id: String = "",
    val name: String = "",
    val start: String = "08:00",
    val ende: String = "17:00",
    val arbeitstage: Map<String, Boolean> = defaultZeitArbeitstage(),
    val pausen: List<ZeitprofilPause> = emptyList()
)

@Serializable
internal data class ZeitprofilConfig(
    val profile: List<PersonalZeitprofil> = emptyList(),
    val zuordnungen: Map<String, String> = emptyMap()
)

private fun zeitApiPart(value: String): String =
    js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String

internal object ZeitApi {
    private fun firmenId(): String =
        LoginService.getUser()?.firmenId.orEmpty().trim()
            .ifBlank { GlobalManager.currentFirmenId.trim() }

    suspend fun loadMonth(month: ZeitMonth): List<All> {
        val raw = runCatching {
            LumaticaApiManager.call(
                "personalkontingent/month-json/${zeitApiPart(firmenId())}/${month.year}/${month.month}"
            )
        }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return emptyList()
        return runCatching { zeitJson.decodeFromString(ListSerializer(All.AllNames.serializer()), raw) }
            .getOrDefault(emptyList())
    }

    suspend fun loadYear(year: Int): List<All> {
        val raw = runCatching {
            LumaticaApiManager.call("personalkontingent/year-json/${zeitApiPart(firmenId())}/$year")
        }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return emptyList()
        return runCatching { zeitJson.decodeFromString(ListSerializer(All.AllNames.serializer()), raw) }
            .getOrDefault(emptyList())
    }

    suspend fun loadMyMonth(month: ZeitMonth): List<All> {
        val raw = runCatching {
            LumaticaApiManager.call("personalkontingent/my-month-json/${month.year}/${month.month}")
        }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return emptyList()
        return runCatching { zeitJson.decodeFromString(ListSerializer(All.AllNames.serializer()), raw) }
            .getOrDefault(emptyList())
    }

    suspend fun loadMyYear(year: Int): List<All> {
        val raw = runCatching {
            LumaticaApiManager.call("personalkontingent/my-year-json/$year")
        }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return emptyList()
        return runCatching { zeitJson.decodeFromString(ListSerializer(All.AllNames.serializer()), raw) }
            .getOrDefault(emptyList())
    }

    suspend fun loadProfiles(): ZeitprofilConfig {
        val raw = runCatching {
            LumaticaApiManager.call("personalkontingent/profile-json/${zeitApiPart(firmenId())}")
        }.getOrDefault("")
        if (raw.isBlank() || raw == "null") return ZeitprofilConfig()
        return runCatching { zeitJson.decodeFromString(ZeitprofilConfig.serializer(), raw) }
            .getOrDefault(ZeitprofilConfig())
    }

    suspend fun loadAbsences(users: List<All>, month: ZeitMonth, fullYear: Boolean): List<All> {
        val userIds = users.map { it.id }.toSet()
        val period = if (fullYear) month.year.toString() else "${month.year}/${month.month}"
        val raw = runCatching {
            LumaticaApiManager.call("personalkontingent/ist-abwesenheiten/${zeitApiPart(firmenId())}/$period")
        }.getOrDefault("")
        val absences = if (raw.isBlank()) emptyList() else
            runCatching { zeitJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
        return absences
            .filter { it.userId.ifBlank { it.kontakt } in userIds && zeitIsVisibleAbsence(it) }
    }

    suspend fun loadMyBookedAbsences(year: Int): List<All> {
        val raw = runCatching {
            LumaticaApiManager.call("personalkontingent/my-ist-abwesenheiten/$year")
        }.getOrDefault("")
        return if (raw.isBlank()) emptyList() else
            runCatching { zeitJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
    }

    suspend fun saveProfiles(config: ZeitprofilConfig): Boolean {
        val body = zeitJson.encodeToString(ZeitprofilConfig.serializer(), config)
        val raw = runCatching {
            LumaticaApiManager.postJson(
                "personalkontingent/profile-save-json/${zeitApiPart(firmenId())}",
                body
            )
        }.getOrDefault("")
        val saved = raw.contains("\"ok\":true") || raw.contains("\"ok\": true")
        if (saved) GlobalManager.markJsonChanged("zeit", "zeiterfassung/zeitprofile.json")
        return saved
    }
}

internal data class ZeitMonth(val year: Int, val month: Int) {
    fun shift(delta: Int): ZeitMonth {
        val next = Date(year, month - 1 + delta, 1)
        return ZeitMonth(next.getFullYear().toInt(), next.getMonth().toInt() + 1)
    }
}

private fun zeitSingleTimelineMonths(anchorMonth: ZeitMonth): List<ZeitMonth> =
    (1..12).map { month -> ZeitMonth(anchorMonth.year, month) }

private data class ZeitDayState(
    val label: String = "",
    val colorHex: String? = null
)

private data class ZeitPopupState(
    val userId: String,
    val iso: String
)

@Composable
fun CUSZeitverwaltung(art: String = "CUS", initialMode: String = "Team") {
    val refreshVersion = maxOf(
        LocalScreenRefreshVersion.current,
        GlobalManager.jsonDomainVersion("zeit"),
        GlobalManager.jsonDomainVersion("urlaub"),
        GlobalManager.jsonDomainVersion("kalender"),
        GlobalManager.jsonDomainVersion("user"),
        GlobalManager.jsonDomainVersion("personal")
    )
    val scope = rememberCoroutineScope()
    var users by remember { mutableStateOf<List<All>>(emptyList()) }
    var vacations by remember { mutableStateOf<List<All>>(emptyList()) }
    var month by remember { mutableStateOf(currentZeitMonth()) }
    var mode by remember(initialMode) { mutableStateOf(initialMode) }
    var selectedUserId by remember { mutableStateOf("") }
    var popup by remember { mutableStateOf<ZeitPopupState?>(null) }
    var loading by remember { mutableStateOf(true) }
    var timeDays by remember { mutableStateOf<List<All>>(emptyList()) }
    var profileConfig by remember { mutableStateOf(ZeitprofilConfig()) }
    var selectedProfileId by remember { mutableStateOf("") }
    var savingProfiles by remember { mutableStateOf(false) }
    var profileMessage by remember { mutableStateOf("") }

    suspend fun reload() {
        loading = true
        val loadedUsers = UrlaubsApi.loadUsers().sortedWith(
            compareBy<All> { it.personalnummer.ifBlank { "\uFFFF" } }
                .thenBy { zeitUserName(it) }
        )
        users = loadedUsers
        vacations = ZeitApi.loadAbsences(loadedUsers, month, mode == "Einzel")
            .filter(::kontingentAbsenceIsEffective)
        timeDays = (when {
            else -> {
                profileConfig = ZeitApi.loadProfiles().let { loaded ->
                    if (loaded.profile.isEmpty()) ZeitprofilConfig(profile = listOf(defaultZeitprofil())) else loaded
                }
                if (mode == "Zeitprofile") emptyList()
                else if (mode == "Einzel") ZeitApi.loadYear(month.year)
                else ZeitApi.loadMonth(month)
            }
        }).filter { it.datum <= zeitTodayIso() }
        loading = false
    }

    LaunchedEffect(month, mode, refreshVersion) { reload() }
    LaunchedEffect(users) {
        if (selectedUserId.isBlank() || users.none { it.id == selectedUserId }) {
            selectedUserId = users.firstOrNull()?.id.orEmpty()
        }
    }
    LaunchedEffect(profileConfig.profile, selectedProfileId) {
        if (selectedProfileId.isBlank() || profileConfig.profile.none { it.id == selectedProfileId }) {
            selectedProfileId = profileConfig.profile.firstOrNull()?.id.orEmpty()
        }
    }

    Style(ZeitverwaltungStyles)
    Div({ classes(ZeitverwaltungStyles.page) }) {
        Div({ classes(ZeitverwaltungStyles.toolbar) }) {
            Div({ classes(ZeitverwaltungStyles.toolbarLeft) }) {
                if (mode != "Zeitprofile") Div({ classes(ZeitverwaltungStyles.modeSwitch) }) {
                    LumaticaButton(
                        label = "Teamansicht",
                        onClick = { mode = "Team" },
                        variant = LumaticaButtonVariant.State,
                        isActive = mode == "Team",
                        heightPx = 32
                    )
                    LumaticaButton(
                        label = "Einzelansicht",
                        onClick = { mode = "Einzel" },
                        variant = LumaticaButtonVariant.State,
                        isActive = mode == "Einzel",
                        heightPx = 32
                    )
                    if (mode == "Zeitprofile") LumaticaButton(
                        label = "Zeitprofile",
                        onClick = { mode = "Zeitprofile" },
                        variant = LumaticaButtonVariant.State,
                        isActive = mode == "Zeitprofile",
                        heightPx = 32
                    )
                }
            }
            Div({ classes(ZeitverwaltungStyles.toolbarCenter) }) {
                if (mode != "Zeitprofile") {
                if (mode == "Einzel") {
                    Div({ classes(ZeitverwaltungStyles.userDropdown) }) {
                        AppDropdown(
                            options = users.map { All(key = it.id, label = zeitUserName(it)) },
                            selectedValue = selectedUserId.ifBlank { null },
                            onChange = {
                                selectedUserId = it
                                popup = null
                            },
                            placeholder = "Mitarbeiter wählen",
                            appearance = AppDropdownAppearance.Surface
                        )
                    }
                }
                ZeitNavButton("images/left.svg", "Zurück") {
                    month = if (mode == "Einzel") {
                        ZeitMonth(month.year - 1, month.month)
                    } else {
                        month.shift(-1)
                    }
                }
                Span({ classes(ZeitverwaltungStyles.monthLabel) }) {
                    Text(if (mode == "Einzel") month.year.toString() else "${zeitMonthName(month.month)} ${month.year}")
                }
                ZeitNavButton("images/right.svg", "Weiter") {
                    month = if (mode == "Einzel") {
                        ZeitMonth(month.year + 1, month.month)
                    } else {
                        month.shift(1)
                    }
                }
                }
            }
        }

        if (loading) {
            Div({ classes(ZeitverwaltungStyles.loading) }) { Text("Zeitdaten werden geladen.") }
        } else if (mode == "Zeitprofile") {
            ZeitprofileView(
                users = users,
                config = profileConfig,
                selectedProfileId = selectedProfileId,
                saving = savingProfiles,
                message = profileMessage,
                onSelectProfile = { selectedProfileId = it },
                onConfigChange = {
                    profileConfig = it
                    profileMessage = ""
                },
                onAddProfile = {
                    val profile = PersonalZeitprofil(
                        id = "zeitprofil-${Date().getTime().toLong()}",
                        name = "Zeitprofil ${profileConfig.profile.size + 1}"
                    )
                    profileConfig = profileConfig.copy(profile = profileConfig.profile + profile)
                    selectedProfileId = profile.id
                    profileMessage = ""
                },
                onSave = {
                    if (!savingProfiles) {
                        savingProfiles = true
                        profileMessage = ""
                        scope.launch {
                            val saved = ZeitApi.saveProfiles(profileConfig)
                            profileMessage = if (saved) {
                                "Zeitprofile gespeichert."
                            } else {
                                "Zeitprofile konnten nicht gespeichert werden."
                            }
                            savingProfiles = false
                        }
                    }
                }
            )
        } else if (mode == "Team") {
            ZeitTeamMonth(
                users = users,
                vacations = vacations,
                timeDays = timeDays,
                month = month,
                profileConfig = profileConfig,
                onOpenDay = { userId, iso -> popup = ZeitPopupState(userId, iso) }
            )
        } else {
            ZeitSingleTimeline(
                user = users.firstOrNull { it.id == selectedUserId },
                vacations = vacations,
                timeDays = timeDays,
                anchorMonth = month,
                profileConfig = profileConfig,
                onOpenDay = { iso -> popup = ZeitPopupState(selectedUserId, iso) }
            )
        }
        popup?.let { state ->
            ZeitDayPopup(
                state = state,
                users = users,
                timeDays = timeDays,
                vacations = vacations,
                profileConfig = profileConfig,
                onClose = { popup = null },
                onShiftDate = { delta -> popup = state.copy(iso = zeitShiftIso(state.iso, delta)) }
            )
        }
    }
}

@Composable
private fun ZeitTeamMonth(
    users: List<All>,
    vacations: List<All>,
    timeDays: List<All>,
    month: ZeitMonth,
    profileConfig: ZeitprofilConfig,
    onOpenDay: (String, String) -> Unit
) {
    val days = zeitDaysInMonth(month.year, month.month)
    val prefix = "${month.year}-${month.month.toString().padStart(2, '0')}-"
    val columns = "minmax(260px, 320px) repeat($days, minmax(42px, 1fr))"
    Div({ classes(ZeitverwaltungStyles.tableScroll) }) {
        Div({
            classes(ZeitverwaltungStyles.table)
            style {
                property("grid-template-columns", columns)
                minWidth((260 + days * 42).px)
            }
        }) {
            Div({ classes(ZeitverwaltungStyles.identityHeader) }) {
                Span { Text("Mitarbeiter") }
                Span({ classes(ZeitverwaltungStyles.identityHeaderMeta) }) { Text("Stundensumme") }
            }
            (1..days).forEach { day ->
                val date = Date(month.year, month.month - 1, day)
                val iso = "$prefix${day.toString().padStart(2, '0')}"
                val isToday = iso == zeitTodayIso()
                val isWeekend = date.getDay().toInt() in setOf(0, 6)
                Div({
                    classes(ZeitverwaltungStyles.dayHeader)
                    if (isWeekend) classes(ZeitverwaltungStyles.weekend)
                    if (isToday) {
                        style {
                            property("box-shadow", "inset 0 0 0 2px ${UISettings.ACCENT_HEX}")
                        }
                    }
                }) {
                    Span({ classes(ZeitverwaltungStyles.weekday) }) { Text(zeitWeekday(date.getDay().toInt())) }
                    Span({
                        classes(ZeitverwaltungStyles.dayNumber)
                        if (isToday) style { color(UISettings.ACCENT) }
                    }) { Text(day.toString()) }
                }
            }
            users.forEach { user ->
                Div({ classes(ZeitverwaltungStyles.identityCell) }) {
                    UserAvatarCircle(user = user, fallbackLabel = zeitUserName(user), sizePx = 34)
                    Span({ classes(ZeitverwaltungStyles.userName) }) { Text(zeitUserName(user)) }
                    Span({
                        classes(ZeitverwaltungStyles.hourBalance)
                        style { color(Color("#84e1a7")) }
                    }) {
                        Text(zeitHourSummary(user.id, timeDays, vacations, month, profileConfig))
                    }
                }
                (1..days).forEach { day ->
                    val iso = "$prefix${day.toString().padStart(2, '0')}"
                    val record = zeitRecordFor(user.id, iso, timeDays)
                    val hasBooking = record != null
                    ZeitDayCell(
                        state = zeitDayState(user.id, iso, vacations),
                        isToday = iso == zeitTodayIso(),
                        isWeekend = zeitParseDate(iso).getDay().toInt() in setOf(0, 6),
                        hasBooking = hasBooking,
                        nonWorkdayBooking = hasBooking && !zeitProfileWorksOnUser(user.id, iso, profileConfig),
                        hoverRows = kontingentDayRows(user.id, iso, record, vacations, profileConfig),
                        onClick = { onOpenDay(user.id, iso) }
                    )
                }
            }
        }
    }
}

@Composable
private fun ZeitSingleTimeline(
    user: All?,
    vacations: List<All>,
    timeDays: List<All>,
    anchorMonth: ZeitMonth,
    profileConfig: ZeitprofilConfig,
    onOpenDay: (String) -> Unit
) {
    if (user == null) {
        Div({ classes(ZeitverwaltungStyles.loading) }) { Text("Kein Mitarbeiter ausgewählt.") }
        return
    }
    val months = zeitSingleTimelineMonths(anchorMonth)
    val columns = "minmax(190px, 220px) repeat(31, minmax(42px, 1fr))"
    Div({ classes(ZeitverwaltungStyles.tableScroll) }) {
        Div({
            classes(
                ZeitverwaltungStyles.table,
                ZeitverwaltungStyles.timelineTable
            )
            style {
                property("grid-template-columns", columns)
                minWidth((190 + 31 * 42).px)
            }
        }) {
            months.forEach { rowMonth ->
                val rowPrefix = "${rowMonth.year}-${rowMonth.month.toString().padStart(2, '0')}-"
                val daysInMonth = zeitDaysInMonth(rowMonth.year, rowMonth.month)
                val monthValues = zeitHourSummaryValues(
                    user.id,
                    timeDays,
                    vacations,
                    rowMonth,
                    profileConfig
                )
                val isCurrentMonth = rowMonth.year == Date().getFullYear().toInt() &&
                    rowMonth.month == Date().getMonth().toInt() + 1
                Div({
                    classes(
                        ZeitverwaltungStyles.identityCell,
                        ZeitverwaltungStyles.timelineIdentityCell
                    )
                }) {
                    Div({ classes(ZeitverwaltungStyles.monthIdentityText) }) {
                        Span({
                            classes(ZeitverwaltungStyles.userName)
                            if (isCurrentMonth) classes(ZeitverwaltungStyles.currentMonthName)
                        }) { Text(zeitMonthShortName(rowMonth.month)) }
                        Span({
                            classes(
                                ZeitverwaltungStyles.hourBalance,
                                ZeitverwaltungStyles.timelineHourBalance
                            )
                        }) {
                            Text(
                                zeitFormatHours(monthValues.first)
                            )
                        }
                    }
                }
                (1..31).forEach { day ->
                    if (day > daysInMonth) {
                        Div({ classes(ZeitverwaltungStyles.emptyDayCell) }) {}
                    } else {
                        val iso = "$rowPrefix${day.toString().padStart(2, '0')}"
                        val record = zeitRecordFor(user.id, iso, timeDays)
                        val hasBooking = record != null
                        ZeitTimelineDayCell(
                            state = zeitDayState(user.id, iso, vacations),
                            iso = iso,
                            hasBooking = hasBooking,
                            nonWorkdayBooking = hasBooking && !zeitProfileWorksOnUser(user.id, iso, profileConfig),
                            hoverRows = kontingentDayRows(user.id, iso, record, vacations, profileConfig),
                            onClick = { onOpenDay(iso) }
                        )
                    }
                }
            }
            val annualValues = months
                .map { rowMonth ->
                    zeitHourSummaryValues(user.id, timeDays, vacations, rowMonth, profileConfig)
                }
                .fold(0 to 0) { total, monthValues ->
                    (total.first + monthValues.first) to (total.second + monthValues.second)
                }
            Div({
                classes(
                    ZeitverwaltungStyles.identityCell,
                    ZeitverwaltungStyles.timelineIdentityCell,
                    ZeitverwaltungStyles.yearSummaryCell
                )
            }) {
                Div({ classes(ZeitverwaltungStyles.yearSummaryContent) }) {
                    Span({ classes(ZeitverwaltungStyles.yearSummaryLabel) }) {
                        Text("Jahresübersicht")
                    }
                    Span({ classes(ZeitverwaltungStyles.yearSummaryValue) }) {
                        Text(zeitFormatHours(annualValues.first))
                    }
                }
            }
            Div({
                style {
                    property("grid-column", "2 / -1")
                    minWidth(0.px)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                }
            }) {
                PersonalIstLegend(singleLine = true)
            }
        }
    }
}

@Composable
private fun ZeitTimelineDayCell(
    state: ZeitDayState,
    iso: String,
    hasBooking: Boolean,
    nonWorkdayBooking: Boolean,
    hoverRows: List<Pair<String, String>>,
    onClick: () -> Unit
) {
    val date = zeitParseDate(iso)
    val weekend = date.getDay().toInt() in setOf(0, 6)
    val nonWorkday = date.getDay().toInt() == 0 || zeitIsHoliday(iso)
    val bookingColor = ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.SOLL_ERFUELLT)
    val markerColor = when {
        nonWorkday -> null
        state.colorHex != null -> state.colorHex
        hasBooking -> bookingColor
        else -> null
    }
    val warning = nonWorkdayBooking
    val titleText = when {
        state.label.isNotBlank() -> state.label
        nonWorkdayBooking -> "Buchung an einem arbeitsfreien Tag"
        hasBooking -> "Zeitbuchung"
        weekend -> "Wochenende"
        else -> "Keine Zeitbuchung"
    }

    Div({
        classes(
            ZeitverwaltungStyles.dayCell,
            ZeitverwaltungStyles.timelineDayCell
        )
        if (nonWorkday) classes(ZeitverwaltungStyles.nonWorkdayText)
        title(titleText)
        style {
            if (markerColor != null) {
                backgroundColor(Color(markerColor))
            }
        }
        onClick { onClick() }
    }) {
        Div({ classes(ZeitverwaltungStyles.timelineDayContent) }) {
            Span({ classes(ZeitverwaltungStyles.timelineCellWeekday) }) {
                Text(zeitWeekday(date.getDay().toInt()))
            }
            Span({ classes(ZeitverwaltungStyles.timelineDayNumber) }) {
                Text(date.getDate().toInt().toString())
            }
            if (warning) {
                Span({ classes(ZeitverwaltungStyles.timelineWarning) }) { Text("!") }
            }
        }
        if (hoverRows.isNotEmpty()) {
            Div({ classes(ZeitverwaltungStyles.cellHoverCard) }) {
                hoverRows.forEach { row ->
                    Div({ classes(ZeitverwaltungStyles.cellHoverRow) }) {
                        Span({ classes(ZeitverwaltungStyles.cellHoverLabel) }) { Text(row.first) }
                        Span({ classes(ZeitverwaltungStyles.cellHoverValue) }) { Text(row.second) }
                    }
                }
            }
        }
    }
}

@Composable
private fun ZeitDayCell(
    state: ZeitDayState,
    isToday: Boolean,
    isWeekend: Boolean,
    hasBooking: Boolean,
    nonWorkdayBooking: Boolean = false,
    hoverRows: List<Pair<String, String>> = emptyList(),
    onClick: () -> Unit
) {
    val cellColor = when {
        state.colorHex != null -> state.colorHex
        nonWorkdayBooking || hasBooking ->
            ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.SOLL_ERFUELLT)
        else -> null
    }
    Div({
        classes(ZeitverwaltungStyles.dayCell)
        if (isWeekend) classes(ZeitverwaltungStyles.weekend)
        attr("title", state.label)
        if (cellColor != null) style { backgroundColor(Color(cellColor)) }
        if (isToday) {
            style {
                property("box-shadow", "inset 0 0 0 2px ${UISettings.ACCENT_HEX}")
            }
        }
        onClick { onClick() }
    }) {
        if (nonWorkdayBooking) {
            Span({
                classes(ZeitverwaltungStyles.inlineWarning)
                style {
                    color(Color.white)
                }
            }) { Text("!") }
        }
        if (hoverRows.isNotEmpty()) {
            Div({ classes(ZeitverwaltungStyles.cellHoverCard) }) {
                hoverRows.forEach { row ->
                    Div({ classes(ZeitverwaltungStyles.cellHoverRow) }) {
                        Span({ classes(ZeitverwaltungStyles.cellHoverLabel) }) { Text(row.first) }
                        Span({ classes(ZeitverwaltungStyles.cellHoverValue) }) { Text(row.second) }
                    }
                }
            }
        }
    }
}

@Composable
private fun ZeitDayPopup(
    state: ZeitPopupState,
    users: List<All>,
    timeDays: List<All>,
    vacations: List<All>,
    profileConfig: ZeitprofilConfig,
    onClose: () -> Unit,
    onShiftDate: (Int) -> Unit
) {
    val record = zeitRecordFor(state.userId, state.iso, timeDays)
    val timeline = kontingentDayRows(state.userId, state.iso, record, vacations, profileConfig)
    Div({ classes(ZeitverwaltungStyles.popupOverlay) }) {
        Div({ classes(ZeitverwaltungStyles.popupBox) }) {
            Div({ classes(ZeitverwaltungStyles.popupHeader) }) {
                Button(attrs = {
                    classes(ZeitverwaltungStyles.closeButton)
                    onClick { onClose() }
                }) { Text("x") }
            }
            Div({ classes(ZeitverwaltungStyles.popupNavRow) }) {
                ZeitNavButton("images/left.svg", "Vorheriger Tag") { onShiftDate(-1) }
                H3({ classes(ZeitverwaltungStyles.popupTitle) }) { Text(zeitCompactHeader(state.iso)) }
                ZeitNavButton("images/right.svg", "Nächster Tag") { onShiftDate(1) }
            }
            Div({ classes(ZeitverwaltungStyles.popupTable) }) {
                if (timeline.isEmpty()) {
                    Div({ classes(ZeitverwaltungStyles.popupTableRow) }) {
                        Span({ classes(ZeitverwaltungStyles.popupTableCell) }) { Text("Keine Zeitbuchung") }
                        Span({ classes(ZeitverwaltungStyles.popupTableValue) }) { Text("-") }
                    }
                } else {
                    timeline.forEach { row ->
                        Div({ classes(ZeitverwaltungStyles.popupTableRow) }) {
                            Span({ classes(ZeitverwaltungStyles.popupTableCell) }) { Text(row.first) }
                            Span({ classes(ZeitverwaltungStyles.popupTableValue) }) { Text(row.second) }
                        }
                    }
                }
            }
        }
    }
}

@Composable
internal fun ZeitMobileDayPopup(
    userId: String,
    iso: String,
    timeDays: List<All>,
    vacations: List<All>,
    profileConfig: ZeitprofilConfig,
    onClose: () -> Unit,
    onShiftDate: (Int) -> Unit
) {
    Style(ZeitverwaltungStyles)
    ZeitDayPopup(
        state = ZeitPopupState(userId, iso),
        users = emptyList(),
        timeDays = timeDays,
        vacations = vacations,
        profileConfig = profileConfig,
        onClose = onClose,
        onShiftDate = onShiftDate
    )
}

@Composable
private fun ZeitSummaryStrip(summary: String) {
    Div({ classes(ZeitverwaltungStyles.summaryStrip) }) {
        Text(summary)
    }
}

@Composable
private fun ZeitMeta(label: String, value: String) {
    Div({ classes(ZeitverwaltungStyles.metaItem) }) {
        Span({ classes(ZeitverwaltungStyles.metaLabel) }) { Text(label) }
        Span({ classes(ZeitverwaltungStyles.metaValue) }) { Text(value) }
    }
}

@Composable
private fun ZeitNavButton(icon: String, tooltip: String, onClick: () -> Unit) {
    Button(attrs = {
        classes(ZeitverwaltungStyles.navButton)
        attr("title", tooltip)
        onClick { onClick() }
    }) {
        Img(src = icon, attrs = { classes(ZeitverwaltungStyles.navIcon) })
    }
}

private fun zeitDayState(userId: String, iso: String, vacations: List<All>): ZeitDayState {
    if (zeitIsHoliday(iso)) {
        return ZeitDayState(
            label = ZustandManager.Zeit.textFor(ZustandManager.Zeit.FEIERTAG),
            colorHex = ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.FEIERTAG)
        )
    }
    zeitAbsenceFor(userId, iso, vacations)?.let { absence ->
        zeitAbsenceCode(absence) ?: return@let
        val reference = kontingentAbsenceReference(absence, iso)
        return ZeitDayState(
            label = reference.label,
            colorHex = reference.colorHex
        )
    }
    return ZeitDayState()
}

internal data class ZeitMobileDayInfo(
    val colorHex: String? = null,
    val label: String = "",
    val hoursLabel: String = "",
    val warning: Boolean = false,
    val weekend: Boolean = false,
    val today: Boolean = false
)

internal data class ZeitMobileMonthSummary(
    val actual: String,
    val target: String,
    val delta: String,
    val colorHex: String
)

internal fun zeitMobileMonthLabel(month: ZeitMonth): String =
    "${zeitMonthName(month.month)} ${month.year}"

internal fun zeitMobileDayInfo(
    userId: String,
    iso: String,
    timeDays: List<All>,
    vacations: List<All>,
    profileConfig: ZeitprofilConfig
): ZeitMobileDayInfo {
    val date = zeitParseDate(iso)
    val weekend = date.getDay().toInt() in setOf(0, 6)
    val state = zeitDayState(userId, iso, vacations)
    val record = zeitRecordFor(userId, iso, timeDays)
    val hasBooking = record != null
    val nonWorkdayBooking = hasBooking && !zeitProfileWorksOnUser(userId, iso, profileConfig)
    val color = when {
        state.colorHex != null -> state.colorHex
        nonWorkdayBooking || hasBooking ->
            ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.SOLL_ERFUELLT)
        else -> null
    }
    val label = when {
        state.label.isNotBlank() -> state.label
        nonWorkdayBooking -> "Buchung an einem arbeitsfreien Tag"
        hasBooking -> "Zeitbuchung"
        weekend -> "Wochenende"
        else -> "Keine Zeitbuchung"
    }
    return ZeitMobileDayInfo(
        colorHex = color,
        label = label,
        hoursLabel = zeitDayHoursLabel(userId, iso, timeDays),
        warning = nonWorkdayBooking,
        weekend = weekend,
        today = iso == zeitTodayIso()
    )
}

internal fun zeitMobileDayRows(
    userId: String,
    iso: String,
    timeDays: List<All>,
    vacations: List<All>,
    profileConfig: ZeitprofilConfig
): List<Pair<String, String>> =
    kontingentDayRows(userId, iso, zeitRecordFor(userId, iso, timeDays), vacations, profileConfig)

internal fun zeitMobileProfileHourRange(
    userId: String,
    iso: String,
    timeDays: List<All>,
    profileConfig: ZeitprofilConfig
): IntRange? {
    val record = zeitRecordFor(userId, iso, timeDays)
    val profileId = record?.zeitprofilId?.takeIf { it.isNotBlank() }
        ?: profileConfig.zuordnungen[userId].orEmpty()
    val profile = profileConfig.profile.firstOrNull { it.id == profileId } ?: return null
    if (!zeitProfileWorksOn(profile, iso)) return null
    val start = zeitTimeSeconds(profile.start) ?: return null
    val end = zeitTimeSeconds(profile.ende) ?: return null
    if (end <= start) return null
    val firstHour = (start / 3600).coerceIn(0, 23)
    val lastHour = ((end - 1) / 3600).coerceIn(firstHour, 23)
    return firstHour..lastHour
}

internal fun zeitMobileMonthSummary(
    userId: String,
    timeDays: List<All>,
    vacations: List<All>,
    month: ZeitMonth,
    profileConfig: ZeitprofilConfig
): ZeitMobileMonthSummary {
    val values = zeitHourSummaryValues(userId, timeDays, vacations, month, profileConfig)
    val delta = values.first - values.second
    return ZeitMobileMonthSummary(
        actual = zeitFormatHours(values.first),
        target = zeitFormatHours(values.second),
        delta = zeitFormatSignedHours(delta),
        colorHex = zeitHourSummaryColor(userId, timeDays, vacations, month, profileConfig)
    )
}

private fun zeitTerminUserId(termin: All): String =
    termin.userId.ifBlank { termin.kontakt }

private fun zeitAbsenceDays(termin: All): List<String> =
    termin.tage.takeIf { it.isNotEmpty() } ?: listOf(termin.datum).filter { it.isNotBlank() }

private fun zeitAbsenceCode(termin: All): Int? {
    val abwesenheitsart = termin.abwesenheitsart.ifBlank { termin.bezeichnung }.trim().lowercase()
    return when (abwesenheitsart) {
        "freizeitausgleich" -> ZustandManager.Zeit.FREIZEITAUSGLEICH
        "unbezahlt frei" -> ZustandManager.Zeit.FEHLTAG
        "sonderurlaub" -> ZustandManager.Zeit.URLAUB
        "urlaub" -> ZustandManager.Zeit.URLAUB
        else -> when (termin.art.ifBlank { termin.bezeichnung }.trim().lowercase()) {
            "urlaub" -> ZustandManager.Zeit.URLAUB
            "krank", "krankheit", "krankmeldung", "krankenschein" -> ZustandManager.Zeit.KRANK
            else -> null
        }
    }
}

private fun zeitAbsenceCreditsTarget(termin: All): Boolean {
    val abwesenheitsart = termin.abwesenheitsart.ifBlank { termin.bezeichnung }.trim().lowercase()
    return when (abwesenheitsart) {
        "unbezahlt frei" -> false
        "freizeitausgleich",
        "urlaub",
        "sonderurlaub" -> true
        "krank", "krankheit", "krankmeldung", "krankenschein" -> true
        else -> zeitAbsenceCode(termin) in setOf(ZustandManager.Zeit.URLAUB, ZustandManager.Zeit.KRANK)
    }
}

private fun zeitIsCreditedAbsence(termin: All): Boolean =
    zeitAbsenceCode(termin) != null &&
        zeitAbsenceCreditsTarget(termin) &&
        (
            ZustandManager.Abwesenheit.isKrankheit(zeitAbsenceType(termin)) ||
                termin.zustand == ZustandManager.Urlaub.GENEHMIGT
            )

private fun zeitIsVisibleAbsence(termin: All): Boolean =
    zeitAbsenceCode(termin) != null &&
        termin.urlaubstag != false

private fun zeitAbsenceFor(userId: String, iso: String, absences: List<All>): All? =
    if (iso > zeitTodayIso()) null
    else kontingentAbsenceFor(userId, iso, absences.filter(::zeitIsVisibleAbsence))

internal fun zeitTimeOffRows(
    userId: String,
    iso: String,
    absences: List<All>,
    profileConfig: ZeitprofilConfig
): List<Pair<String, String>> {
    val absence = zeitAbsenceFor(userId, iso, absences) ?: return emptyList()
    val type = zeitAbsenceType(absence)
    val rows = kontingentAbsenceReference(absence, iso).rows.toMutableList()
    val isTimeOff = type.equals("Freizeitausgleich", ignoreCase = true)
    if (zeitIsCreditedAbsence(absence)) {
        rows += "Gebucht" to if (absence.stunden > 0.0) {
            "+${zeitFormatDecimalHours(absence.stunden)}"
        } else {
            "-"
        }
    }
    if (!isTimeOff) return rows
    if (!absence.zeitkontoAbgerechnet) {
        return rows
    }
    val booked = (-absence.zeitkontoStunden).coerceAtLeast(0.0)
    val released = absence.zeitkontoFreigegebenStunden.coerceAtLeast(0.0)
    return rows + buildList {
        add("Buchungsart" to absence.zeitkontoBuchung.ifBlank { "Zeitausgleich gebucht" })
        if (booked > 0.0) add("Zeitkonto" to "-${zeitFormatDecimalHours(booked)}")
        if (released > 0.0) add("Reserve freigegeben" to zeitFormatDecimalHours(released))
    }
}

private fun zeitFormatDecimalHours(hours: Double): String {
    val totalMinutes = (hours * 60).toInt()
    val wholeHours = totalMinutes / 60
    val minutes = totalMinutes % 60
    return if (minutes == 0) "$wholeHours Std." else "$wholeHours Std. ${minutes} Min."
}

private fun zeitAbsenceDayIsDue(iso: String, profile: PersonalZeitprofil?): Boolean {
    val today = zeitTodayIso()
    if (iso < today) return true
    if (iso > today) return false
    val profileEnd = zeitTimeSeconds(profile?.ende.orEmpty()) ?: return false
    val now = Date()
    val nowSeconds = now.getHours().toInt() * 3600 +
        now.getMinutes().toInt() * 60 +
        now.getSeconds().toInt()
    return nowSeconds >= profileEnd
}

private fun zeitAbsenceCodeFor(userId: String, iso: String, absences: List<All>): Int? =
    zeitAbsenceFor(userId, iso, absences)?.let(::zeitAbsenceCode)

private fun zeitAbsenceType(termin: All): String =
    kontingentAbsenceType(termin)

private fun zeitAbsenceStatusText(termin: All): String =
    ZustandManager.Abwesenheit.statusText(zeitAbsenceType(termin), termin.zustand)

private fun zeitAbsenceStateLabel(termin: All): String =
    listOf(zeitAbsenceType(termin), zeitAbsenceStatusText(termin))
        .filter { it.isNotBlank() }
        .joinToString(" ")

private fun zeitAbsenceStatusPriority(zustand: Int): Int =
    when (ZustandManager.Urlaub.fromCode(zustand)) {
        ZustandManager.Urlaub.GENEHMIGT -> 5
        ZustandManager.Urlaub.IN_BEARBEITUNG -> 4
        ZustandManager.Urlaub.BEANTRAGT -> 3
        ZustandManager.Urlaub.VERWEIGERT -> 2
        ZustandManager.Urlaub.STORNIERT -> 1
        else -> 0
    }

private fun zeitCreditedAbsenceFor(userId: String, iso: String, absences: List<All>): All? =
    absences.firstOrNull { termin ->
        zeitTerminUserId(termin) == userId &&
            zeitIsCreditedAbsence(termin) &&
            iso in zeitAbsenceDays(termin)
    }

private fun zeitMonthHourLabels(userId: String, timeDays: List<All>, month: ZeitMonth): Map<String, String> {
    val days = zeitDaysInMonth(month.year, month.month)
    return (1..days).associate { day ->
        val iso = "${month.year}-${month.month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}"
        iso to zeitDayHoursLabel(userId, iso, timeDays)
    }.filterValues { it.isNotBlank() }
}

private fun zeitYearHourLabels(userId: String, timeDays: List<All>, year: Int): Map<String, String> =
    (1..12).flatMap { month ->
        (1..zeitDaysInMonth(year, month)).map { day ->
            val iso = "$year-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}"
            iso to zeitDayHoursLabel(userId, iso, timeDays)
        }
    }.filter { it.second.isNotBlank() }.toMap()

private fun zeitMonthDayTexts(
    userId: String,
    timeDays: List<All>,
    vacations: List<All>,
    month: ZeitMonth,
    config: ZeitprofilConfig
): Map<String, String> {
    val profile = config.profile.firstOrNull { it.id == config.zuordnungen[userId] }
    val prefix = "${month.year}-${month.month.toString().padStart(2, '0')}-"
    return (1..zeitDaysInMonth(month.year, month.month)).mapNotNull { day ->
        val iso = "$prefix${day.toString().padStart(2, '0')}"
        val worked = zeitWorkedSeconds(zeitRecordFor(userId, iso, timeDays))
        val absence = zeitAbsenceFor(userId, iso, vacations)
        val absenceLabel = absence?.let { kontingentAbsenceReference(it, iso).label }.orEmpty()
        val absenceApproved = absence?.let(::kontingentAbsenceIsEffective) == true
        val bookedTimeOffSeconds = ((-absence?.zeitkontoStunden.orZero()).coerceAtLeast(0.0) * 3600).toInt()
        if (worked > 0) {
            val timeOffLabel = if (absence?.zeitkontoAbgerechnet == true && bookedTimeOffSeconds > 0) {
                "\nZeitausgleich -${zeitFormatDuration(bookedTimeOffSeconds)}"
            } else {
                ""
            }
            iso to "${zeitFormatDuration(worked)}$timeOffLabel"
        } else {
            val absenceCode = absence?.let(::zeitAbsenceCode) ?: return@mapNotNull null
            iso to if (absence.zeitkontoAbgerechnet && bookedTimeOffSeconds > 0) {
                "${absence.zeitkontoBuchung.ifBlank { "Zeitausgleich gebucht" }}\n-${zeitFormatDuration(bookedTimeOffSeconds)}"
            } else if (
                absenceApproved &&
                absenceCode != ZustandManager.Zeit.FREIZEITAUSGLEICH &&
                zeitAbsenceCreditsTarget(absence) &&
                zeitAbsenceDayIsDue(iso, profile)
            ) {
                "$absenceLabel\n+${zeitFormatDecimalHours(absence.stunden)}"
            } else {
                absenceLabel
            }
        }
    }.toMap()
}

private fun Double?.orZero(): Double = this ?: 0.0

private fun zeitMonthStatusMarkers(
    userId: String,
    timeDays: List<All>,
    vacations: List<All>,
    month: ZeitMonth,
    config: ZeitprofilConfig
): Map<String, String> =
    (1..zeitDaysInMonth(month.year, month.month)).associate { day ->
        val iso = "${month.year}-${month.month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}"
        iso to zeitDayMarkerColor(userId, iso, timeDays, vacations, config)
    }.filterValues { it.isNotBlank() }

private fun zeitYearStatusMarkers(
    userId: String,
    timeDays: List<All>,
    vacations: List<All>,
    year: Int,
    config: ZeitprofilConfig
): Map<String, String> =
    (1..12).flatMap { month ->
        (1..zeitDaysInMonth(year, month)).map { day ->
            val iso = "$year-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}"
            iso to zeitDayMarkerColor(userId, iso, timeDays, vacations, config)
        }
    }.filter { it.second.isNotBlank() }.toMap()

private fun zeitMonthStatusMarkerTexts(
    userId: String,
    timeDays: List<All>,
    vacations: List<All>,
    month: ZeitMonth,
    config: ZeitprofilConfig
): Map<String, String> =
    zeitMonthStatusMarkers(userId, timeDays, vacations, month, config)
        .filterKeys { iso ->
            zeitRecordFor(userId, iso, timeDays) != null && !zeitProfileWorksOnUser(userId, iso, config)
        }
        .mapValues { "!" }

private fun zeitYearStatusMarkerTexts(
    userId: String,
    timeDays: List<All>,
    vacations: List<All>,
    year: Int,
    config: ZeitprofilConfig
): Map<String, String> =
    zeitYearStatusMarkers(userId, timeDays, vacations, year, config)
        .filterKeys { iso ->
            zeitRecordFor(userId, iso, timeDays) != null && !zeitProfileWorksOnUser(userId, iso, config)
        }
        .mapValues { "!" }

private fun zeitDayMarkerColor(
    userId: String,
    iso: String,
    timeDays: List<All>,
    vacations: List<All>,
    config: ZeitprofilConfig
): String {
    zeitDayState(userId, iso, vacations).colorHex?.let { return it }
    if (zeitRecordFor(userId, iso, timeDays) != null) {
        return ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.SOLL_ERFUELLT)
    }
    return ""
}

private fun zeitDayHoursLabel(userId: String, iso: String, timeDays: List<All>): String =
    zeitRecordFor(userId, iso, timeDays)?.let { zeitFormatDuration(zeitWorkedSeconds(it)) }.orEmpty()

private fun zeitRecordFor(userId: String, iso: String, timeDays: List<All>): All? =
    timeDays.firstOrNull { it.userId == userId && it.datum == iso }

private fun zeitWorkedSeconds(day: All?): Int {
    if (day == null) return 0
    if (day.bruttozeitSekunden > 0 || day.pauseSekunden > 0 || day.zeitprofilId.isNotBlank()) {
        return day.arbeitszeitSekunden.coerceAtLeast(0)
    }
    return zeitSessions(day).sumOf { session ->
        val start = zeitTimeSeconds(session.first) ?: 0
        val end = zeitTimeSeconds(session.second) ?: start
        (end - start).coerceAtLeast(0)
    }
}

private fun zeitSessionRecords(day: All?): List<All> {
    if (day == null) return emptyList()
    if (day.sessions.isNotEmpty()) return day.sessions
    return zeitSessions(day).map { session ->
        val start = zeitTimeSeconds(session.first) ?: 0
        val end = zeitTimeSeconds(session.second) ?: start
        All(
            uhrzeitVon = session.first,
            uhrzeitBis = session.second,
            dauerSekunden = (end - start).coerceAtLeast(0),
            pauseSekunden = 0,
            arbeitszeitSekunden = (end - start).coerceAtLeast(0)
        )
    }
}

internal fun zeitTimelineRows(day: All?): List<Pair<String, String>> {
    if (day == null) return emptyList()
    val rows = mutableListOf<Triple<Int, Int, Pair<String, String>>>()
    zeitSessionRecords(day).forEachIndexed { index, session ->
        val start = zeitTimeSeconds(session.uhrzeitVon) ?: 0
        val end = zeitTimeSeconds(session.uhrzeitBis) ?: start
        rows += Triple(start, index * 100, "Kommen" to zeitFormatClock(session.uhrzeitVon))
        session.pausen.forEachIndexed { pauseIndex, pause ->
            val pauseStart = zeitTimeSeconds(pause.uhrzeitVon) ?: start
            rows += Triple(
                pauseStart,
                index * 100 + pauseIndex + 10,
                "Pause ${pauseIndex + 1}" to "${zeitFormatClock(pause.uhrzeitVon)} / ${zeitFormatMinutes(pause.dauerSekunden)}"
            )
        }
        day.stempelungen
            .filter { it.typ == "bereichwechsel" }
            .forEach { wechsel ->
                val seconds = zeitTimeSeconds(wechsel.uhrzeitVon) ?: return@forEach
                if (seconds in start..end) {
                    rows += Triple(seconds, index * 100 + 60, "Bereich" to "${zeitFormatClock(wechsel.uhrzeitVon)} / ${wechsel.bereich.ifBlank { "-" }}")
                }
            }
        rows += Triple(end, index * 100 + 90, "Gehen" to zeitFormatClock(session.uhrzeitBis))
    }
    if (rows.isEmpty()) {
        day.stempelungen.sortedBy { it.uhrzeitVon }.forEachIndexed { index, event ->
            val label = when (event.typ) {
                "einstempeln" -> "Kommen"
                "ausstempeln" -> "Gehen"
                "bereichwechsel" -> "Bereich"
                else -> event.bezeichnung.ifBlank { event.typ }
            }
            val value = if (event.typ == "bereichwechsel") {
                "${zeitFormatClock(event.uhrzeitVon)} / ${event.bereich.ifBlank { "-" }}"
            } else {
                zeitFormatClock(event.uhrzeitVon)
            }
            rows += Triple(zeitTimeSeconds(event.uhrzeitVon) ?: index, index, label to value)
        }
    }
    rows += Triple(Int.MAX_VALUE - 2, 0, "Arbeitszeit" to zeitFormatDuration(zeitWorkedSeconds(day)))
    rows += Triple(Int.MAX_VALUE - 1, 0, "Pausezeit" to zeitFormatDuration(day.pauseSekunden))
    return rows.sortedWith(compareBy<Triple<Int, Int, Pair<String, String>>> { it.first }.thenBy { it.second })
        .map { it.third }
}

internal fun zeitDailyOvertimeRows(
    userId: String,
    iso: String,
    day: All?,
    config: ZeitprofilConfig
): List<Pair<String, String>> {
    val worked = zeitWorkedSeconds(day)
    if (worked <= 0) return emptyList()
    val profile = config.profile.firstOrNull { it.id == config.zuordnungen[userId] }
    val target = if (!zeitIsHoliday(iso) && zeitProfileWorksOn(profile, iso)) {
        zeitProfileDailyTargetSeconds(profile).ifBlankZero { 8 * 60 * 60 }
    } else {
        0
    }
    val overtime = worked - target
    return if (overtime > 0) {
        listOf("Überstunden" to "${zeitFormatHours(overtime)} Std.")
    } else {
        emptyList()
    }
}

private fun zeitSessions(day: All?): List<Pair<String, String>> {
    val sessions = mutableListOf<Pair<String, String>>()
    var open: String? = null
    day?.stempelungen.orEmpty()
        .sortedBy { it.uhrzeitVon }
        .forEach { event ->
            when (event.typ) {
                "einstempeln" -> if (open == null) open = event.uhrzeitVon
                "ausstempeln" -> {
                    val start = open
                    if (start != null) {
                        val startSeconds = zeitTimeSeconds(start)
                        val endSeconds = zeitTimeSeconds(event.uhrzeitVon)
                        if (startSeconds != null && endSeconds != null && endSeconds >= startSeconds) {
                            sessions += start to event.uhrzeitVon
                        }
                        open = null
                    }
                }
            }
        }
    return sessions
}

private fun zeitFormatDuration(seconds: Int): String {
    val positive = seconds.coerceAtLeast(0)
    val roundedMinutes = (positive + 59) / 60
    val hours = roundedMinutes / 60
    val minutes = roundedMinutes % 60
    return "${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}"
}

private fun zeitFormatMinutes(seconds: Int): String {
    val minutes = (seconds.coerceAtLeast(0) + 59) / 60
    return "$minutes Min"
}

private fun zeitFormatClock(value: String): String {
    val seconds = zeitTimeSeconds(value) ?: return value.ifBlank { "-" }
    val roundedMinutes = (seconds + 59) / 60
    val hours = (roundedMinutes / 60) % 24
    val minutes = roundedMinutes % 60
    return "${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}"
}

private fun zeitTimeSeconds(value: String): Int? {
    val parts = value.split(":")
    val hours = parts.getOrNull(0)?.toIntOrNull() ?: return null
    val minutes = parts.getOrNull(1)?.toIntOrNull() ?: return null
    val seconds = parts.getOrNull(2)?.toIntOrNull() ?: 0
    return hours * 3600 + minutes * 60 + seconds
}

private fun zeitHourSummary(userId: String, timeDays: List<All>, vacations: List<All>, month: ZeitMonth, config: ZeitprofilConfig): String {
    val actual = zeitHourSummaryValues(userId, timeDays, vacations, month, config).first
    return zeitFormatHours(actual)
}

private fun zeitHourSummaryColor(userId: String, timeDays: List<All>, vacations: List<All>, month: ZeitMonth, config: ZeitprofilConfig): String {
    val actual = zeitHourSummaryValues(userId, timeDays, vacations, month, config).first
    return if (actual > 0) {
        ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.SOLL_ERFUELLT)
    } else {
        ZustandManager.Zeit.keineBuchungColorHex()
    }
}

internal fun zeitHourSummaryValues(userId: String, timeDays: List<All>, vacations: List<All>, month: ZeitMonth, config: ZeitprofilConfig): Pair<Int, Int> {
    val days = zeitDaysInMonth(month.year, month.month)
    val profile = config.profile.firstOrNull { it.id == config.zuordnungen[userId] }
    val dailyTarget = zeitProfileDailyTargetSeconds(profile)
    val effectiveDailyTarget = dailyTarget.ifBlankZero { 8 * 60 * 60 }
    val prefix = "${month.year}-${month.month.toString().padStart(2, '0')}-"
    val recordsByDate = timeDays
        .filter { it.userId == userId && it.datum.startsWith(prefix) }
        .associateBy { it.datum }
    val actual = (1..days).sumOf { day ->
        val iso = "$prefix${day.toString().padStart(2, '0')}"
        val workedSeconds = zeitWorkedSeconds(recordsByDate[iso])
        when {
            workedSeconds > 0 -> workedSeconds
            zeitCreditedAbsenceFor(userId, iso, vacations) != null ->
                ((zeitCreditedAbsenceFor(userId, iso, vacations)?.stunden ?: 0.0) * 3600).toInt()
            else -> 0
        }
    }
    var target = 0
    (1..zeitTargetLastDay(month)).forEach { day ->
        val iso = "${month.year}-${month.month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}"
        if (!zeitIsHoliday(iso) && zeitProfileWorksOn(profile, iso)) {
            target += effectiveDailyTarget
        }
    }
    return actual to target
}

private fun zeitTargetLastDay(month: ZeitMonth): Int {
    val days = zeitDaysInMonth(month.year, month.month)
    val today = Date()
    val currentYear = today.getFullYear().toInt()
    val currentMonth = today.getMonth().toInt() + 1
    return when {
        month.year == currentYear && month.month == currentMonth -> today.getDate().toInt().coerceIn(1, days)
        month.year > currentYear || (month.year == currentYear && month.month > currentMonth) -> 0
        else -> days
    }
}

private fun Int.ifBlankZero(fallback: () -> Int): Int = if (this > 0) this else fallback()

internal fun defaultZeitArbeitstage(): Map<String, Boolean> = mapOf(
    "mo" to true,
    "di" to true,
    "mi" to true,
    "do" to true,
    "fr" to true,
    "sa" to false,
    "so" to false
)

internal val zeitArbeitstagKeys: List<Pair<String, String>> = listOf(
    "mo" to "Mo",
    "di" to "Di",
    "mi" to "Mi",
    "do" to "Do",
    "fr" to "Fr",
    "sa" to "Sa",
    "so" to "So"
)

private fun zeitProfileDayKey(iso: String): String =
    when (zeitParseDate(iso).getDay().toInt()) {
        0 -> "so"
        1 -> "mo"
        2 -> "di"
        3 -> "mi"
        4 -> "do"
        5 -> "fr"
        6 -> "sa"
        else -> "mo"
    }

private fun zeitProfileWorksOn(profile: PersonalZeitprofil?, iso: String): Boolean {
    val days = profile?.arbeitstage?.takeIf { it.isNotEmpty() } ?: defaultZeitArbeitstage()
    return days[zeitProfileDayKey(iso)] == true
}

private fun zeitProfileWorksOnUser(userId: String, iso: String, config: ZeitprofilConfig): Boolean {
    val profile = zeitProfileForUser(userId, config)
    return zeitProfileWorksOn(profile, iso)
}

private fun zeitProfileForUser(userId: String, config: ZeitprofilConfig): PersonalZeitprofil? =
    config.profile.firstOrNull { it.id == config.zuordnungen[userId] }

private fun zeitProfileDailyTargetSeconds(profile: PersonalZeitprofil?): Int {
    if (profile == null) return 0
    val start = zeitTimeSeconds(profile.start) ?: return 0
    val end = zeitTimeSeconds(profile.ende) ?: return 0
    val pause = profile.pausen.sumOf { pause ->
        val pauseStart = zeitTimeSeconds(pause.von) ?: return@sumOf 0
        val pauseEnd = zeitTimeSeconds(pause.bis) ?: return@sumOf 0
        (pauseEnd - pauseStart).coerceAtLeast(0)
    }
    return (end - start - pause).coerceAtLeast(0)
}

internal fun zeitFormatHours(seconds: Int): String {
    val hours = seconds.toDouble() / 3600.0
    return if (hours % 1.0 == 0.0) hours.toInt().toString() else (kotlin.math.round(hours * 10) / 10).toString().replace(".", ",")
}

internal fun zeitFormatSignedHours(seconds: Int): String {
    val prefix = if (seconds >= 0) "+" else "-"
    return prefix + zeitFormatHours(kotlin.math.abs(seconds))
}

private fun currentZeitMonth(): ZeitMonth {
    val now = Date()
    return ZeitMonth(now.getFullYear().toInt(), now.getMonth().toInt() + 1)
}

private fun zeitTodayIso(): String {
    val now = Date()
    return "${now.getFullYear().toInt()}-${(now.getMonth().toInt() + 1).toString().padStart(2, '0')}-${now.getDate().toInt().toString().padStart(2, '0')}"
}

private fun zeitMonthIso(month: ZeitMonth): String =
    "${month.year}-${month.month.toString().padStart(2, '0')}-01"

private fun zeitDaysInMonth(year: Int, month: Int): Int =
    Date(year, month, 0).getDate().toInt()

private fun zeitMonthCells(year: Int, month: Int): List<String> {
    val first = Date(year, month - 1, 1)
    val offset = (first.getDay().toInt() + 6) % 7
    val days = zeitDaysInMonth(year, month)
    return List(offset) { "" } + (1..days).map { "$year-${month.toString().padStart(2, '0')}-${it.toString().padStart(2, '0')}" }
}

private fun zeitParseDate(iso: String): Date {
    val parts = iso.split("-")
    return Date(
        parts.getOrNull(0)?.toIntOrNull() ?: Date().getFullYear().toInt(),
        (parts.getOrNull(1)?.toIntOrNull() ?: 1) - 1,
        parts.getOrNull(2)?.toIntOrNull() ?: 1
    )
}

private fun zeitShiftIso(iso: String, delta: Int): String {
    val date = zeitParseDate(iso)
    val next = Date(date.getFullYear().toInt(), date.getMonth().toInt(), date.getDate().toInt() + delta)
    return "${next.getFullYear().toInt()}-${(next.getMonth().toInt() + 1).toString().padStart(2, '0')}-${next.getDate().toInt().toString().padStart(2, '0')}"
}

private fun zeitIsHoliday(iso: String): Boolean =
    iso.takeLast(5) in setOf("01-01", "05-01", "10-03", "12-25", "12-26")

private fun zeitFormatDate(iso: String): String {
    val parts = iso.split("-")
    return if (parts.size == 3) "${parts[2]}.${parts[1]}.${parts[0]}" else iso
}

private fun zeitFormatLongDate(iso: String): String {
    val date = zeitParseDate(iso)
    return "${zeitWeekdayLong(date.getDay().toInt())}, ${zeitFormatDate(iso)}"
}

private fun zeitCompactHeader(iso: String): String {
    val date = zeitParseDate(iso)
    return "${zeitWeekday(date.getDay().toInt()).uppercase()} ${zeitFormatDate(iso)}"
}

private fun zeitUserName(user: All): String =
    user.name.ifBlank { listOf(user.vorname, user.nachname).filter { it.isNotBlank() }.joinToString(" ") }
        .ifBlank { user.email.ifBlank { user.id } }

private fun zeitWeekday(jsDay: Int): String =
    listOf("SO", "MO", "DI", "MI", "DO", "FR", "SA").getOrElse(jsDay) { "" }

private fun zeitWeekdayLong(jsDay: Int): String =
    listOf("Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag").getOrElse(jsDay) { "" }

private fun zeitMonthName(month: Int): String =
    listOf("Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember")
        .getOrElse(month - 1) { month.toString() }

private fun zeitMonthShortName(month: Int): String =
    listOf(
        "Jan.", "Feb.", "Mär.", "Apr.", "Mai", "Jun.",
        "Jul.", "Aug.", "Sep.", "Okt.", "Nov.", "Dez."
    ).getOrElse(month - 1) { month.toString() }

private object ZeitverwaltungStyles : StyleSheet() {
    val page by style {
        width(100.percent)
        height(100.percent)
        minHeight(0.px)
        padding(18.px)
        boxSizing("border-box")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(10.px)
        color(Color.white)
        overflow("hidden")
    }
    val toolbar by style {
        flexShrink(0)
        display(DisplayStyle.Grid)
        property("grid-template-columns", "1fr auto 1fr")
        alignItems(AlignItems.Center)
        gap(14.px)
    }
    val toolbarLeft by style { display(DisplayStyle.Flex) }
    val toolbarCenter by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        gap(10.px)
        flexWrap(FlexWrap.Wrap)
    }
    val modeSwitch by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(6.px)
    }
    val userDropdown by style { width(220.px) }
    val monthLabel by style {
        minWidth(150.px)
        textAlign("center")
        fontSize(15.px)
        fontWeight("900")
    }
    val navButton by style {
        width(32.px)
        height(32.px)
        padding(0.px)
        borderRadius(6.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.16))
        backgroundColor(rgba(255, 255, 255, 0.06))
        cursor("pointer")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
    }
    val navIcon by style {
        width(13.px)
        height(13.px)
        property("filter", "brightness(0) invert(1)")
    }
    val loading by style {
        padding(16.px)
        color(rgba(255, 255, 255, 0.68))
    }
    val tableScroll by style {
        minHeight(0.px)
        flexGrow(1)
        overflow("auto")
    }
    val table by style {
        width(100.percent)
        display(DisplayStyle.Grid)
        property("grid-auto-rows", "60px")
    }
    val timelineTable by style {
        property("grid-auto-rows", "72px")
    }
    val identityHeader by style {
        position(Position.Sticky)
        left(0.px)
        top(0.px)
        property("z-index", "4")
        padding(0.px, 12.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween)
        backgroundColor(Color("#20242a"))
        property("border-right", "1px solid rgba(255,255,255,0.14)")
        property("border-bottom", "1px solid rgba(255,255,255,0.14)")
        fontSize(15.px)
        fontWeight("800")
        boxSizing("border-box")
    }
    val identityHeaderMeta by style {
        color(rgba(255, 255, 255, 0.48))
        fontSize(13.px)
        fontWeight("700")
    }
    val dayHeader by style {
        position(Position.Sticky)
        top(0.px)
        property("z-index", "3")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        backgroundColor(Color("#20242a"))
        property("border-right", "1px solid rgba(255,255,255,0.08)")
        property("border-bottom", "1px solid rgba(255,255,255,0.14)")
    }
    val weekday by style {
        color(rgba(255, 255, 255, 0.44))
        fontSize(13.px)
        fontWeight("700")
    }
    val dayNumber by style {
        fontSize(16.px)
        fontWeight("900")
    }
    val identityCell by style {
        position(Position.Sticky)
        left(0.px)
        property("z-index", "2")
        padding(0.px, 10.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(9.px)
        backgroundColor(Color("#191d22"))
        property("border-right", "1px solid rgba(255,255,255,0.14)")
        property("border-bottom", "1px solid rgba(255,255,255,0.08)")
        boxSizing("border-box")
    }
    val userName by style {
        property("flex", "1 1 auto")
        overflow("hidden")
        property("text-overflow", "ellipsis")
        property("white-space", "nowrap")
        fontSize(15.px)
        fontWeight("800")
    }
    val hourBalance by style {
        property("margin-left", "auto")
        fontSize(15.px)
        fontWeight("900")
        property("white-space", "nowrap")
    }
    val monthIdentityText by style {
        width(100.percent)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween)
        gap(10.px)
        minWidth(0.px)
    }
    val timelineIdentityCell by style {
        padding(0.px, 10.px)
        property("border-right", "1px solid rgba(255,255,255,0.18)")
        property("border-bottom", "2px solid rgba(255,255,255,0.48)")
        property("background-clip", "padding-box")
    }
    val timelineHourBalance by style {
        color(rgba(255, 255, 255, 0.54))
        fontSize(13.px)
        fontWeight("800")
    }
    val currentMonthName by style {
        color(UISettings.ACCENT)
    }
    val yearSummaryCell by style {
        position(Position.Sticky)
        left(0.px)
        minHeight(72.px)
        backgroundColor(Color("#20242a"))
    }
    val yearSummaryContent by style {
        width(100.percent)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        textAlign("center")
        gap(5.px)
    }
    val yearSummaryLabel by style {
        color(rgba(255, 255, 255, 0.62))
        fontSize(12.px)
        fontWeight("800")
    }
    val yearSummaryValue by style {
        color(Color.white)
        fontSize(13.px)
        fontWeight("900")
        property("white-space", "nowrap")
    }
    val positiveBalance by style {
        color(Color("#84e1a7"))
    }
    val negativeBalance by style {
        color(UISettings.ACCENT)
    }
    val dayCell by style {
        position(Position.Relative)
        cursor("pointer")
        property("border-right", "1px solid rgba(255,255,255,0.065)")
        property("border-bottom", "1px solid rgba(255,255,255,0.08)")
        boxSizing("border-box")
        overflow("visible")
        property("z-index", "1")
        self + ":hover" style {
            property("box-shadow", "inset 0 0 0 2px rgba(255,255,255,0.36)")
            property("z-index", "9998")
        }
        self + ":hover > div:last-child" style {
            display(DisplayStyle.Flex)
        }
    }
    val emptyDayCell by style {
        position(Position.Relative)
        backgroundColor(rgba(255, 255, 255, 0.018))
        property("border-right", "1px solid rgba(255,255,255,0.18)")
        property("border-bottom", "2px solid rgba(255,255,255,0.48)")
        property("background-clip", "padding-box")
        boxSizing("border-box")
    }
    val timelineDayCell by style {
        property("border-right", "1px solid rgba(255,255,255,0.18)")
        property("border-bottom", "2px solid rgba(255,255,255,0.48)")
        property("background-clip", "padding-box")
    }
    val timelineDayContent by style {
        width(100.percent)
        height(100.percent)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        gap(3.px)
    }
    val timelineCellWeekday by style {
        fontSize(11.px)
        fontWeight("900")
        lineHeight("1")
        property("opacity", "0.68")
    }
    val timelineDayNumber by style {
        fontSize(15.px)
        fontWeight("900")
        lineHeight("1")
    }
    val nonWorkdayText by style {
        color(UISettings.ACCENT)
    }
    val timelineWarning by style {
        position(Position.Absolute)
        right(5.px)
        bottom(4.px)
        width(14.px)
        height(14.px)
        borderRadius(50.percent)
        backgroundColor(rgba(0, 0, 0, 0.34))
        color(Color.white)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        fontSize(10.px)
        fontWeight("900")
        lineHeight("1")
    }
    val weekend by style {
        backgroundColor(rgba(255, 255, 255, 0.035))
        color(rgba(255, 255, 255, 0.35))
    }
    val dayMarkerDot by style {
        position(Position.Absolute)
        left(50.percent)
        top(50.percent)
        width(12.px)
        height(12.px)
        borderRadius(50.percent)
        property("transform", "translate(-50%, -50%)")
        property("box-shadow", "0 0 9px currentColor")
    }
    val missingBookingDot by style {
        position(Position.Absolute)
        left(50.percent)
        top(50.percent)
        width(17.px)
        height(17.px)
        borderRadius(50.percent)
        property("transform", "translate(-50%, -50%)")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        fontSize(12.px)
        fontWeight("900")
        lineHeight("1")
        property("box-shadow", "0 0 8px currentColor")
    }
    val inlineWarning by style {
        position(Position.Absolute)
        left(50.percent)
        top(50.percent)
        property("transform", "translate(-50%, -50%)")
        fontSize(14.px)
        fontWeight("900")
        lineHeight("1")
    }
    val dayHourText by style {
        position(Position.Absolute)
        left(50.percent)
        top(50.percent)
        property("transform", "translate(-50%, -50%)")
        color(rgba(255, 255, 255, 0.74))
        fontSize(14.px)
        fontWeight("900")
        property("white-space", "nowrap")
    }
    val cellHoverCard by style {
        position(Position.Absolute)
        left(50.percent)
        top(100.percent)
        property("transform", "translateX(-50%)")
        marginTop(8.px)
        width(280.px)
        minHeight(128.px)
        maxHeight(240.px)
        display(DisplayStyle.None)
        flexDirection(FlexDirection.Column)
        gap(4.px)
        padding(12.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.16))
        borderRadius(6.px)
        backgroundColor(Color("#15191e"))
        property("box-shadow", "0 12px 28px rgba(0,0,0,0.42)")
        property("z-index", "9999")
        property("pointer-events", "none")
        boxSizing("border-box")
        overflow("hidden")
    }
    val cellHoverRow by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.SpaceBetween)
        gap(8.px)
        fontSize(14.px)
        lineHeight("1.25")
    }
    val cellHoverLabel by style {
        color(rgba(255, 255, 255, 0.58))
        fontWeight("800")
    }
    val cellHoverValue by style {
        color(Color.white)
        fontWeight("900")
        textAlign("right")
    }
    val singleCalendar by style {
        minHeight(0.px)
        flexGrow(1)
        overflow("auto")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        gap(10.px)
        padding(0.px, 6.px)
        boxSizing("border-box")
    }
    val summaryStrip by style {
        alignSelf(AlignSelf.Center)
        minHeight(28.px)
        padding(0.px, 12.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.14))
        borderRadius(6.px)
        backgroundColor(rgba(255, 255, 255, 0.055))
        color(Color.white)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        fontSize(13.px)
        fontWeight("900")
        property("white-space", "nowrap")
    }
    val popupOverlay by style {
        position(Position.Fixed)
        left(0.px)
        top(0.px)
        right(0.px)
        bottom(0.px)
        property("z-index", "10000")
        backgroundColor(rgba(0, 0, 0, 0.52))
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        padding(18.px)
        boxSizing("border-box")
    }
    val popupBox by style {
        width(460.px)
        maxWidth(92.vw)
        maxHeight(92.vh)
        overflow("auto")
        padding(18.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.14))
        borderRadius(6.px)
        backgroundColor(Color("#191d22"))
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(10.px)
        boxSizing("border-box")
    }
    val popupHeader by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.FlexEnd)
        height(32.px)
    }
    val popupNavRow by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "32px 1fr 32px")
        alignItems(AlignItems.Center)
        gap(10.px)
        minHeight(36.px)
    }
    val popupTitle by style {
        margin(0.px)
        fontSize(15.px)
        fontWeight("900")
        property("white-space", "nowrap")
        textAlign("center")
    }
    val closeButton by style {
        width(32.px)
        height(32.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.16))
        borderRadius(5.px)
        backgroundColor(rgba(255, 255, 255, 0.06))
        color(Color.white)
        cursor("pointer")
    }
    val popupTable by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "1fr 1fr")
        property("grid-auto-rows", "minmax(38px, max-content)")
        alignContent(AlignContent.Start)
        gap(0.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
        borderRadius(5.px)
        overflow("hidden")
    }
    val popupTableRow by style {
        display(DisplayStyle.Contents)
    }
    val popupTableCell by style {
        padding(9.px, 12.px)
        color(rgba(255, 255, 255, 0.62))
        fontSize(15.px)
        fontWeight("800")
        property("border-bottom", "1px solid rgba(255,255,255,0.08)")
        backgroundColor(rgba(255, 255, 255, 0.03))
        property("white-space", "nowrap")
    }
    val popupTableValue by style {
        padding(9.px, 12.px)
        color(Color.white)
        fontSize(15.px)
        fontWeight("900")
        textAlign("right")
        property("border-bottom", "1px solid rgba(255,255,255,0.08)")
        backgroundColor(rgba(255, 255, 255, 0.02))
        property("white-space", "nowrap")
    }
    val popupSection by style {
        padding(14.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
        borderRadius(6.px)
        backgroundColor(rgba(255, 255, 255, 0.035))
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(10.px)
    }
    val sessionCard by style {
        padding(12.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
        borderRadius(6.px)
        backgroundColor(rgba(0, 0, 0, 0.16))
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(8.px)
        boxSizing("border-box")
    }
    val sessionTitle by style {
        color(Color.white)
        fontSize(13.px)
        fontWeight("900")
    }
    val sessionInfoGrid by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
        gap(8.px)
    }
    val sectionTitle by style {
        fontSize(13.px)
        fontWeight("900")
    }
    val bookingSummary by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "repeat(4, minmax(0, 1fr))")
        gap(10.px)
    }
    val metaItem by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(4.px)
        padding(10.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.10))
        borderRadius(5.px)
        backgroundColor(rgba(255, 255, 255, 0.04))
    }
    val metaLabel by style {
        color(rgba(255, 255, 255, 0.52))
        fontSize(10.px)
        fontWeight("800")
    }
    val metaValue by style {
        fontSize(13.px)
        fontWeight("900")
    }
    val emptyText by style {
        color(rgba(255, 255, 255, 0.52))
        fontSize(12.px)
        fontWeight("700")
    }
}
